import { Box, Theme, Tooltip, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { WasteManifestSource } from "generated-graphql/graphql";

import { DOCUMENT_SOURCE_COLOR, primarySourceLabel } from "./manifestSourceDisplay";

export const SOURCE_LETTER: Record<WasteManifestSource, string> = {
  [WasteManifestSource.EManifest]: "E",
  [WasteManifestSource.Vendor]: "V",
  [WasteManifestSource.ManifestDocument]: "D",
};

/**
 * The accent color identifying each source pillar (matches the Data Sources
 * chips): e-Manifest green, Vendor blue, Document indigo.
 */
export function getSourceAccent(theme: Theme, source: WasteManifestSource): string {
  switch (source) {
    case WasteManifestSource.EManifest:
      return theme.palette.success.main;
    case WasteManifestSource.Vendor:
      return theme.palette.info.main;
    case WasteManifestSource.ManifestDocument:
      return DOCUMENT_SOURCE_COLOR;
  }
}

export interface SourceLetterBoxProps {
  source: WasteManifestSource;
  /** Box edge length in px (the list view uses 20, inline field markers 15). */
  size?: number;
  /** Hover tooltip. Defaults to "From <source>"; pass null to disable. */
  tooltip?: string | null;
  /** Click to open a provenance popover; renders as a button when set. */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Names the document source "Uploaded bill of lading" instead of "Uploaded Manifest Document". */
  isBillOfLading?: boolean;
}

/**
 * Small lettered box (`E` / `V` / `D`) identifying a data source, tinted in
 * the source's accent color — the same mark as the Sources column on the
 * manifest list, reused inline in the drawer wherever a value's provenance
 * needs calling out.
 */
export function SourceLetterBox({
  source,
  size = 15,
  tooltip,
  onClick,
  isBillOfLading = false,
}: SourceLetterBoxProps) {
  const theme = useTheme();
  const sourceLabel = primarySourceLabel(source, isBillOfLading);
  const accent = getSourceAccent(theme, source);

  const box = (
    <Box
      component="span"
      role={onClick ? "button" : undefined}
      aria-label={`Source: ${sourceLabel}`}
      onClick={(event) => {
        // Don't trigger the InfoBox copy/expand modal.
        event.stopPropagation();
        onClick?.(event);
      }}
      sx={{
        width: size,
        height: size,
        borderRadius: 0.75,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        // 0.6 keeps the 9px lettering of the original 16px box at 15px.
        fontSize: Math.round(size * 0.6),
        fontWeight: 600,
        lineHeight: 1,
        color: accent,
        backgroundColor: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.5)}`,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {SOURCE_LETTER[source]}
    </Box>
  );

  if (tooltip === null) return box;
  return (
    <Tooltip title={tooltip ?? `From ${sourceLabel}`} placement="top" enterDelay={300}>
      {box}
    </Tooltip>
  );
}
